<template>
  <div class="audit-report-details">
    <div class="assessment-header">
      <div class="header-content">
        <div class="header-text">
          <h2 class="assessment-title">Audit Report Analysis</h2>
          <p class="assessment-subtitle">
            Review extracted findings and financial statement tables
          </p>
        </div>
      </div>
    </div>

    <div class="audit-mode-toggle" role="tablist">
      <button
        type="button"
        class="mode-btn"
        :class="{ active: activeTab === 'analysis' }"
        role="tab"
        :aria-selected="activeTab === 'analysis'"
        @click="activeTab = 'analysis'"
      >
        Analysis
        <span v-if="analysisSections.length" class="mode-count">{{ analysisSections.length }}</span>
      </button>
      <button
        type="button"
        class="mode-btn"
        :class="{ active: activeTab === 'tables' }"
        role="tab"
        :aria-selected="activeTab === 'tables'"
        @click="activeTab = 'tables'"
      >
        Tables
        <span v-if="tables.length" class="mode-count">{{ tables.length }}</span>
      </button>
    </div>

    <div v-if="activeTab === 'analysis'" class="audit-tab-panel" role="tabpanel">
      <div v-if="analysisSections.length" class="analysis-list">
        <div
          v-for="section in analysisSections"
          :key="section.key"
          class="details-section"
        >
          <AnalysisCard :title="section.title" :items="section.items" :editable="false" />
        </div>
      </div>
      <div v-else class="no-data">
        <h3>No Analysis Findings</h3>
        <p>No analysis items were extracted from this audit report.</p>
      </div>
    </div>

    <div v-else class="audit-tab-panel" role="tabpanel">
      <div v-if="tables.length" class="tables-list">
        <div
          v-for="(table, index) in tables"
          :key="table.id || `table-${index}`"
          class="audit-table-block"
        >
          <h3 class="audit-table-title">{{ table.title || `Table ${index + 1}` }}</h3>
          <div class="audit-table-scroll">
            <SimpleTable :data="table" :expand-arrays="true" variant="financial" />
          </div>
        </div>
      </div>
      <div v-else class="no-data">
        <h3>No Tables</h3>
        <p>No tables were extracted from this audit report.</p>
      </div>
    </div>
  </div>
</template>

<script>
import SimpleTable from '../SimpleTable.vue'
import AnalysisCard from '../creditReport/AnalysisCard.vue'

const ANALYSIS_FIELDS = [
  { key: 'auditor_going_concern', title: 'Auditor Going Concern' },
  { key: 'auditor_modified_opinions', title: 'Auditor Modified Opinions' },
  { key: 'auditor_emphasis_of_matter', title: 'Auditor Emphasis of Matter' },
  { key: 'related_party_transactions', title: 'Related Party Transactions' },
  { key: 'internal_control_weaknesses', title: 'Internal Control Weaknesses' },
  { key: 'revenue_recognition_policies', title: 'Revenue Recognition Policies' },
  { key: 'non_core_or_exceptional_revenue', title: 'Non Core or Exceptional Revenue' },
  { key: 'litigation_contingent_liabilities', title: 'Litigation Contingent Liabilities' },
  { key: 'tax_statutory_compliance', title: 'Tax Statutory Compliance' },
  { key: 'reliance_on_external_support', title: 'Reliance on External Support' },
  { key: 'subsequent_post_balance_sheet_events', title: 'Subsequent Post Balance Sheet Events' },
  { key: 'management_tone_and_language', title: 'Management Tone and Language' },
  { key: 'management_inconsistencies_and_omissions', title: 'Management Inconsistencies and Omissions' }
]

export default {
  name: 'AuditReportDetails',
  components: {
    SimpleTable,
    AnalysisCard
  },
  props: {
    data: {
      type: [Object, Array],
      required: true
    }
  },
  data() {
    return {
      activeTab: 'analysis'
    }
  },
  computed: {
    reportData() {
      if (!this.data) return {};
      if (Array.isArray(this.data)) return this.data[0] || {};
      return this.data;
    },
    analysisSections() {
      const source = this.reportData;
      const sections = ANALYSIS_FIELDS
        .map((field) => ({
          key: field.key,
          title: field.title,
          items: this.normalizeAnalysisItems(source[field.key])
        }))
        .filter((section) => section.items.length > 0);

      const insights = Array.isArray(source.insights) ? source.insights : [];
      const factual = this.normalizeInsightItems(insights.filter((item) => this.getInsightType(item) === 'factual'));
      const theory = this.normalizeInsightItems(insights.filter((item) => this.getInsightType(item) === 'theory'));
      const other = this.normalizeInsightItems(
        insights.filter((item) => {
          const type = this.getInsightType(item);
          return type !== 'factual' && type !== 'theory';
        })
      );

      if (factual.length) {
        sections.push({ key: 'insights_factual', title: 'Factual Insights', items: factual });
      }
      if (theory.length) {
        sections.push({ key: 'insights_theory', title: 'Theory Insights', items: theory });
      }
      if (other.length) {
        sections.push({ key: 'insights', title: 'Insights', items: other });
      }

      return sections;
    },
    tables() {
      const tables = this.reportData.tables;
      return Array.isArray(tables) ? tables : [];
    }
  },
  watch: {
    analysisSections: {
      immediate: true,
      handler(sections) {
        if ((!sections || sections.length === 0) && this.tables.length > 0 && this.activeTab === 'analysis') {
          this.activeTab = 'tables'
        }
      }
    }
  },
  methods: {
    getInsightType(item) {
      if (!item || typeof item !== 'object') return '';
      return String(item.type || '').trim().toLowerCase();
    },
    normalizeAnalysisItems(items) {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => this.formatAnalysisItem(item))
        .filter(Boolean);
    },
    normalizeInsightItems(items) {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => this.formatInsightItem(item))
        .filter(Boolean);
    },
    formatInsightItem(item) {
      if (item === undefined || item === null || item === '') return '';
      if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
      if (typeof item !== 'object') return String(item);

      const heading = String(item.heading || item.title || '').trim();
      const detail = String(item.detail || item.description || item.text || '').trim();
      if (heading && detail) return `${heading} — ${detail}`;
      return heading || detail;
    },
    formatAnalysisItem(item) {
      if (item === undefined || item === null || item === '') return '';
      if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
      if (typeof item === 'object') return this.formatInsightItem(item);
      return String(item);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.audit-report-details {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 100%;
}

.assessment-header {
  padding: 0 0 1.25rem;

  .header-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .header-text {
    .assessment-title {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0 0 0.25rem 0;
      font-family: $brand-font-medium;
      letter-spacing: -0.3px;
      color: #0f172a;
    }

    .assessment-subtitle {
      font-size: 0.875rem;
      margin: 0;
      color: #64748b;
      font-family: $brand-font-regular;
    }
  }
}

.audit-mode-toggle {
  display: flex;
  gap: 0.35rem;
  margin-bottom: 1rem;
  width: fit-content;

  .mode-btn {
    background: transparent;
    border: 1px solid #e2e8f0;
    padding: 0.7rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    color: #6c757d;
    font-family: $brand-font-medium;

    &:hover {
      border-color: $color-primary;
      color: $color-primary;
    }

    &.active {
      background: $color-primary;
      color: $color-secondary;
      border-color: $color-primary;

      .mode-count {
        background: rgba(0, 0, 0, 0.12);
        color: $color-secondary;
      }
    }
  }
}

.mode-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #e9ecef;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.audit-tab-panel {
  min-height: 8rem;
}

.analysis-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.details-section {
  background: #ffffff;
  padding: 1rem 0;
  margin-bottom: 0.5rem;
}

.tables-list {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.audit-table-block {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.audit-table-title {
  margin: 0;
  padding: 1rem 1.25rem 0.85rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
  font-family: $brand-font-medium;
  text-align: center;
  letter-spacing: 0.01em;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.audit-table-scroll {
  overflow-x: auto;
  background: #ffffff;
}

::v-deep .simple-table-container {
  border: none;
  border-radius: 0;
  background: transparent;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  h3 {
    color: #6c757d;
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
    font-family: $brand-font-medium;
  }

  p {
    color: #adb5bd;
    margin: 0;
    font-family: $brand-font-regular;
  }
}

@media (max-width: 768px) {
  .assessment-header {
    .header-text .assessment-title {
      font-size: 1.25rem;
    }
  }

  .audit-mode-toggle {
    width: 100%;

    .mode-btn {
      flex: 1;
      padding: 0.75rem 0.75rem;
    }
  }

  .audit-table-title {
    font-size: 0.9rem;
  }
}
</style>
